import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { adminApi, clearAdminToken, getAdminToken, type AdminSetting } from '../../lib/admin-api';

const SOURCE_LABEL: Record<AdminSetting['source'], { label: string; cls: string }> = {
  d1: { label: 'D1', cls: 'bg-emerald-100 text-emerald-900 border-emerald-300' },
  env: { label: 'env Worker', cls: 'bg-blue-100 text-blue-900 border-blue-300' },
  none: { label: 'Non configuré', cls: 'bg-zinc-100 text-zinc-700 border-zinc-300' },
};

export function AdminSettingsPage() {
  const navigate = useNavigate();
  const [settings, setSettings] = useState<AdminSetting[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ key: string; value: string } | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!getAdminToken()) {
      navigate('/admin/login', { replace: true });
      return;
    }
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function refresh() {
    setLoading(true);
    setError(null);
    try {
      const r = await adminApi.listSettings();
      setSettings(r.settings);
    } catch (err) {
      const e = err as Error & { status?: number };
      if (e.status === 401) {
        navigate('/admin/login', { replace: true });
        return;
      }
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleSave() {
    if (!editing) return;
    setSaving(true);
    try {
      await adminApi.updateSetting(editing.key, editing.value);
      setEditing(null);
      await refresh();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(key: string) {
    if (!confirm(`Effacer la valeur D1 pour "${key}" ? Le Worker retombera sur la valeur env si configurée.`)) {
      return;
    }
    try {
      await adminApi.deleteSetting(key);
      await refresh();
    } catch (err) {
      setError((err as Error).message);
    }
  }

  function logout() {
    clearAdminToken();
    navigate('/admin/login', { replace: true });
  }

  return (
    <div className="space-y-6 max-w-5xl">
      <div className="flex items-center justify-between">
        <Link to="/" className="text-taupe text-sm hover:text-ink">
          ← Retour à la plateforme
        </Link>
        <button
          type="button"
          onClick={logout}
          className="text-xs px-3 py-1.5 rounded border border-border hover:border-gold transition"
        >
          Déconnexion
        </button>
      </div>

      <header>
        <h2 className="font-serif text-3xl font-bold">Console super-admin</h2>
        <nav className="flex flex-wrap gap-2 mt-3 text-sm">
          <Link to="/admin/settings" className="px-3 py-1 rounded border border-gold bg-gold/10 text-ink">Paramètres</Link>
          <Link to="/admin/firms" className="px-3 py-1 rounded border border-border hover:border-gold">Cabinets</Link>
          <Link to="/admin/users" className="px-3 py-1 rounded border border-border hover:border-gold">Utilisateurs</Link>
        </nav>
        <p className="text-taupe mt-3 max-w-3xl text-sm">
          Clés API et secrets injectés au runtime dans tous les agents. Les valeurs définies ici (D1)
          priment sur les <code className="font-mono">wrangler secret</code> (env). Pour ajouter ou
          supprimer une clé du catalogue, modifier <code className="font-mono">apps/api/src/lib/settings.ts</code>.
        </p>
      </header>

      {error && (
        <div className="card border border-red-300 bg-red-50 text-red-900 text-sm">{error}</div>
      )}

      {loading && <div className="text-taupe text-sm">Chargement…</div>}

      {settings && (
        <div className="card !p-0 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-cream/40 text-xs uppercase tracking-wider text-taupe border-b border-border">
                <th className="px-4 py-3 text-left">Clé</th>
                <th className="px-4 py-3 text-left">Description</th>
                <th className="px-4 py-3 text-left">Source</th>
                <th className="px-4 py-3 text-left">Valeur</th>
                <th className="px-4 py-3 text-left">Mis à jour</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {settings.map((s) => {
                const src = SOURCE_LABEL[s.source];
                return (
                  <tr key={s.key} className="border-b border-border/60 last:border-0">
                    <td className="px-4 py-3 font-mono">{s.key}</td>
                    <td className="px-4 py-3 text-xs text-taupe max-w-md">{s.description}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded border ${src.cls}`}>{src.label}</span>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">
                      {s.masked_value ?? <span className="text-taupe italic">— aucune —</span>}
                    </td>
                    <td className="px-4 py-3 text-xs text-taupe">
                      {s.updated_at ? `${formatDate(s.updated_at)} par ${s.updated_by ?? 'super_admin'}` : '—'}
                    </td>
                    <td className="px-4 py-3 text-right space-x-2">
                      <button
                        type="button"
                        onClick={() => setEditing({ key: s.key, value: '' })}
                        className="text-xs px-2 py-1 rounded bg-ink text-cream hover:opacity-90"
                      >
                        {s.configured ? 'Remplacer' : 'Définir'}
                      </button>
                      {s.source === 'd1' && (
                        <button
                          type="button"
                          onClick={() => handleDelete(s.key)}
                          className="text-xs px-2 py-1 rounded border border-red-300 text-red-800 hover:bg-red-50"
                        >
                          Effacer
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <EditModal
          settingKey={editing.key}
          value={editing.value}
          onChange={(v) => setEditing({ ...editing, value: v })}
          onCancel={() => setEditing(null)}
          onSave={handleSave}
          saving={saving}
        />
      )}
    </div>
  );
}

function EditModal({
  settingKey,
  value,
  onChange,
  onCancel,
  onSave,
  saving,
}: {
  settingKey: string;
  value: string;
  onChange: (v: string) => void;
  onCancel: () => void;
  onSave: () => void;
  saving: boolean;
}) {
  return (
    <div className="fixed inset-0 bg-ink/60 flex items-center justify-center p-4 z-50">
      <div className="bg-cream rounded-lg shadow-xl w-full max-w-md">
        <div className="p-5 border-b border-border">
          <div className="text-xs uppercase tracking-wider text-taupe">Éditer paramètre</div>
          <h3 className="font-serif text-xl font-bold font-mono">{settingKey}</h3>
        </div>
        <div className="p-5 space-y-3">
          <label className="block">
            <span className="text-sm font-medium">Nouvelle valeur</span>
            <input
              type="password"
              autoComplete="off"
              autoFocus
              value={value}
              onChange={(e) => onChange(e.target.value)}
              className="w-full border border-border rounded px-3 py-2 font-mono text-sm mt-1"
              placeholder="Coller la nouvelle valeur"
            />
          </label>
          <p className="text-xs text-taupe">
            La valeur sera stockée en D1 et utilisée immédiatement par tous les Workers (via getPlatformSetting).
          </p>
        </div>
        <div className="p-5 border-t border-border flex justify-end gap-2">
          <button type="button" onClick={onCancel} className="btn-secondary">
            Annuler
          </button>
          <button
            type="button"
            onClick={onSave}
            disabled={saving || value.length === 0}
            className="btn-primary"
          >
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  );
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString('fr-FR');
  } catch {
    return iso;
  }
}
